import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import NoteServices from '@/services/note';
import BaseIconSvg from '@/components/bases/BaseIconSvg';
import utils from '@/utils';
import { Button, message, Input } from 'antd';
import useAuth from '@/stores/auth';
import quickModal from '@/utils/quickModal';

const Empty = () => {
  return <div className="text-center p-5">Chưa có ghi chú nào</div>;
};

const Item = (props: any) => {
  const { note, update } = props;
  const [mode, setMode] = useState<string>('read');
  const user = useAuth((state) => state.user);
  const [content, setContent] = useState(note.content);
  const ref = React.useRef<any>(null);

  if (mode === 'edit') {
    return (
      <div className="bg-white cms-soft rounded-lg p-4 mb-2">
        <div className="flex mb-4">
          <div className="text-14 font-bold flex-auto">{note.creator.username}</div>
          <div className="text-10">{utils.safeFormat(note.createdAt)}</div>
        </div>

        <Input.TextArea ref={ref} className="mb-2 text-12" defaultValue={content} rows={4}></Input.TextArea>

        <div className="flex justify-end">
          <BaseIconSvg
            src="/icons/Regular/Edit - icon.svg"
            className="w-3 mr-2 text-gray-400"
            onClick={() => setMode('edit')}
          />

          <BaseIconSvg
            src="/icons/Regular/Check - icon.svg"
            className="w-3 mr-2 cursor-pointer"
            onClick={() => {
              update(note._id, mode, ref?.current?.resizableTextArea?.textArea?.value);
              setContent(ref?.current?.resizableTextArea?.textArea?.value);
              setMode('read');
            }}
          />

          <BaseIconSvg
            src="/icons/Regular/Close - icon.svg"
            className="w-3 mr-2 cursor-pointer"
            onClick={() => setMode('read')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white cms-soft rounded-lg p-4 mb-2">
      <div className="flex mb-4">
        <div className="text-14 font-bold flex-auto">{note.creator.username}</div>
        <div className="text-10">{utils.safeFormat(note.createdAt)}</div>
      </div>

      <div className="text-12 whitespace-pre-wrap">{content}</div>

      {note.creator._id === user._id && (
        <div className="flex justify-end">
          <BaseIconSvg
            src="/icons/Regular/Edit - icon.svg"
            className="w-3 mr-2 cursor-pointer"
            onClick={() => setMode('edit')}
          />

          <BaseIconSvg
            src="/icons/Regular/Trash - icon.svg"
            className="w-3 mr-2 cursor-pointer"
            onClick={() => {
              quickModal.confirm({
                content: 'Bạn có chắc chắn muốn xóa',
                onOk: () => {
                  update(note._id, 'delete');
                },
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

export default function NoteBox() {
  const [notes, setNotes] = useState<any[]>([]);
  const [input, setInput] = useState<string>('');
  const [next, setNext] = useState<string>('');
  const params = useParams();

  const uid = params.uid as string;

  const create = async () => {
    if (input.length < 3) {
      return message.error('Ghi chú phải nhập tối thiểu 3 kí tự');
    }

    if (input.length > 450) {
      return message.error('Ghi chú phải nhập tối đa 450 kí tự');
    }

    const { error, data } = await NoteServices.post({ uid, content: input });

    if (error) {
      return message.error(error.message);
    }

    notes.unshift(data);
    setNotes(notes);
    setInput('');
    message.success('Thêm mới ghi chú thành công');
  };

  const update = async (_id: string, mode: string, content?: string) => {
    if (mode === 'delete') {
      const { error } = await NoteServices.patch({ _id, status: 'deleted' });

      if (error) {
        return message.error(error.message);
      }

      setNotes(notes.filter((n: any) => n._id !== _id));
      return message.success('Xóa ghi chú thành công');
    }

    if (mode === 'edit') {
      const { error } = await NoteServices.patch({ _id, content });

      if (error) {
        return message.error(error.message);
      }

      return message.success('Cập nhật ghi chú thành công');
    }
  };

  const fetchNext = async () => {
    const { error, data, meta } = await NoteServices.get({}, next);

    if (error) {
      return message.error(error.message);
    }

    setNotes(notes.concat(data));
    setNext(meta.next || '');
  };

  useEffect(() => {
    uid &&
      NoteServices.get({ uid, limit: 6 }).then(({ error, data, meta }) => {
        if (error) {
          return message.error(error.message);
        }

        setNotes(data);
        setNext(meta.next || '');
      });
  }, [uid]);

  return (
    <div className="w-full p-2">
      <div className="h-96 overflow-y-auto">
        {notes.map((n) => (
          <Item note={n} update={update} key={n._id} />
        ))}
        {!notes.length && <Empty />}

        <div className="text-center">{next && <Button onClick={fetchNext}>Xem thêm</Button>}</div>
      </div>

      <div className="mt-3 flex rounded-lg border-gray-300 border items-center">
        <Input.TextArea value={input} onChange={(e) => setInput(e.target.value)} className="flex-auto !border-b-0" />

        <BaseIconSvg
          onClick={create}
          src="/icons/Regular/Plus - icon.svg"
          className="cursor-pointer p-2 box-content rounded-r-md"
        />
      </div>
    </div>
  );
}
